// this is going to be the user profile page
import EditOutlined from '@ant-design/icons/EditOutlined';
import HomeOutlined from '@ant-design/icons/HomeOutlined';
import InfoCircleOutlined from '@ant-design/icons/InfoCircleOutlined';
import MoonOutlined from '@ant-design/icons/MoonOutlined';
import SunOutlined from '@ant-design/icons/SunOutlined';
import Breadcrumb from 'antd/es/breadcrumb';
import Button from 'antd/es/button';
import Card from 'antd/es/card';
import Divider from 'antd/es/divider';
import Input from 'antd/es/input';
import Space from 'antd/es/space';
import Switch from 'antd/es/switch';
import Tooltip from 'antd/es/tooltip';
import Typography from 'antd/es/typography';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { styled, useTheme } from 'styled-components';

import { CenterSpin } from '../../../components/CenterSpin';
import { useDarkMode } from '../../../contexts/DarkModeContext';
import { useEditorSettings } from '../../../contexts/EditorSettingsContext';
import { useSessionUser } from '../../../contexts/SessionUserContext';
import { useTeamUsers } from '../../../queries/team-users';
import { useTeamsByIds } from '../../../queries/teams';
import { useUserById } from '../../../queries/users';

const { Title, Text } = Typography;

const PageContainer = styled.div`
  ${({ theme }) => `
  padding: ${theme.paddingXL}px 0;
  max-width: ${theme.controlHeightLG * 20}px;
  margin: 0 auto;
`}
`;

const BreadcrumbContainer = styled.div`
  ${({ theme }) => `
  margin-bottom: ${theme.marginLG}px;
`}
`;

const Section = styled.div`
  ${({ theme }) => `
  margin-bottom: ${theme.marginXL}px;
`}
`;

const ProfileHeader = styled.div`
  ${({ theme }) => `
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${theme.marginLG}px;
`}
`;

const ProfileInfo = styled.div`
  flex: 1;
`;

const EditableField = styled.div`
  ${({ theme }) => `
  display: flex;
  align-items: center;
  gap: ${theme.marginSM}px;
  margin-bottom: ${theme.margin}px;
  padding: ${theme.paddingXXS}px 0;
  
  .ant-input {
    max-width: ${theme.controlHeightLG * 7.5}px;
  }

  &:hover {
    background: ${theme.colorBgTextHover};
    border-radius: ${theme.borderRadiusSM}px;
  }
`}
`;

const TeamItem = styled.div`
  ${({ theme }) => `
  display: inline-block;
  padding: ${theme.paddingXS}px ${theme.paddingSM}px;
  border-radius: ${theme.borderRadiusXS * 3}px;
  transition: all ${theme.motionDurationMid};
  margin-bottom: ${theme.marginXS}px;

  &:hover {
    background: ${theme.colorBgTextHover};
  }
`}
`;

const TeamRole = styled(Text)<{ $isAdmin?: boolean }>`
  ${({ theme, $isAdmin }) => `
  font-size: ${theme.fontSizeSM - 1}px;
  padding: ${theme.paddingXXS / 2}px ${theme.paddingXS}px;
  border-radius: ${theme.borderRadiusSM}px;
  background: ${$isAdmin ? theme.colorPrimaryBg : theme.colorBgTextHover};
  margin-left: ${theme.marginXS}px;
`}
`;

const PreferenceRow = styled.div`
  ${({ theme }) => `
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.paddingXS}px 0;
  margin-bottom: ${theme.marginXS}px;
`}
`;

export const UserProfilePage = () => {
  const { userId } = useSessionUser();
  const { data: user, isLoading: isLoadingUser } = useUserById(userId);
  const { data: teamUsers, isLoading: isLoadingTeams } = useTeamUsers(
    { userIds: userId ? [userId] : [] },
    { enabled: !!userId },
  );
  const teamIds = teamUsers?.items?.map((tu) => tu.teamId) || [];
  const { data: teams, isLoading: isLoadingTeamDetails } = useTeamsByIds(
    teamIds,
    {
      enabled: teamIds.length > 0,
    },
  );
  // single state variable for which field is being edited
  const [editingField, setEditingField] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const theme = useTheme();
  const [isDarkMode, setIsDarkMode] = useDarkMode();
  const { settings: editorSettings, updateSettings: updateEditorSettings } =
    useEditorSettings();

  const isLoading = isLoadingUser || isLoadingTeams || isLoadingTeamDetails;

  const teamMap = useMemo(() => {
    if (!teams?.items) return new Map();
    return new Map(teams.items.map((team) => [team.teamId, team]));
  }, [teams]);

  if (isLoading) return <CenterSpin />;
  if (!user) {
    return (
      <PageContainer>
        <Card>
          <Text type="danger">Failed to load user profile</Text>
        </Card>
      </PageContainer>
    );
  }

  const handleEditStart = (field: string) => {
    setEditingField(field);
    switch (field) {
      case 'name':
        setNewName(user.fullName);
        break;
      case 'email':
        setNewEmail('user@example.com'); // placeholder bc we don't have an email for the user yet
        break;
      case 'password':
        setNewPassword('');
        break;
    }
  };

  const handleUpdate = async (_field: string) => {
    if (!editingField) return;

    setIsUpdating(true);
    try {
      // TODO: implement actual API calls for each field
      setEditingField(null);
    } catch (error) {
      // handle error
    } finally {
      setIsUpdating(false);
    }
  };

  const renderEditableField = (
    field: string,
    label: string,
    value: string,
    placeholder?: string,
  ) => (
    <EditableField>
      <Text strong style={{ minWidth: 80 }}>
        {label}
      </Text>
      {editingField === field ? (
        <>
          <Input
            value={
              field === 'name'
                ? newName
                : field === 'email'
                  ? newEmail
                  : newPassword
            }
            onChange={(e) => {
              switch (field) {
                case 'name':
                  setNewName(e.target.value);
                  break;
                case 'email':
                  setNewEmail(e.target.value);
                  break;
                case 'password':
                  setNewPassword(e.target.value);
                  break;
              }
            }}
            placeholder={placeholder}
            type={field === 'password' ? 'password' : 'text'}
            autoFocus
          />
          <Button
            type="primary"
            size="small"
            onClick={() => handleUpdate(field)}
            loading={isUpdating}
          >
            Save
          </Button>
          <Button size="small" onClick={() => setEditingField(null)}>
            Cancel
          </Button>
        </>
      ) : (
        <>
          {/* TODO: pull actual user password */}
          <Text>{field === 'password' ? '••••••••' : value}</Text>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEditStart(field)}
            size="small"
          />
        </>
      )}
    </EditableField>
  );

  return (
    <PageContainer>
      <BreadcrumbContainer>
        <Breadcrumb
          items={[
            {
              title: (
                <Link to="/">
                  <HomeOutlined />
                </Link>
              ),
            },
            {
              title: 'Profile',
            },
          ]}
        />
      </BreadcrumbContainer>
      <Title
        level={4}
        style={{
          marginBottom: `${theme.marginLG}px`,
          textAlign: 'left',
          fontSize: `${theme.fontSizeXL + 4}px`,
        }}
      >
        Account Details
      </Title>
      <Card>
        <ProfileHeader>
          <ProfileInfo>
            {renderEditableField('name', 'Name', user.fullName)}
            <Text
              type="secondary"
              style={{
                fontSize: `${theme.fontSize}px`,
                display: 'block',
                marginBottom: `${theme.marginXS}px`,
              }}
            >
              Last updated {new Date(user.updatedAt).toLocaleDateString()}
            </Text>
            <Text
              copyable
              style={{
                fontFamily: 'monospace',
                fontSize: `${theme.fontSizeSM}px`,
                display: 'block',
                color: theme.colorTextSecondary,
                background: theme.colorBgTextHover,
                padding: `${theme.paddingXXS}px ${theme.paddingXS}px`,
                borderRadius: `${theme.borderRadiusSM}px`,
                width: 'fit-content',
              }}
            >
              {user.userId}
            </Text>
          </ProfileInfo>
        </ProfileHeader>

        <Divider style={{ margin: `${theme.marginLG}px 0` }} />

        {renderEditableField(
          'email',
          'Email',
          'user@example.com',
          'Enter your email',
        )}
        {renderEditableField(
          'password',
          'Password',
          '••••••••',
          'Enter new password',
        )}

        <Divider style={{ margin: `${theme.marginLG}px 0` }} />

        <Section>
          <Title level={5} style={{ margin: `0 0 ${theme.margin}px 0` }}>
            Preferences
          </Title>
          <PreferenceRow>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: `${theme.marginXS}px`,
              }}
            >
              {isDarkMode ? (
                <MoonOutlined style={{ color: theme.colorText }} />
              ) : (
                <SunOutlined style={{ color: theme.colorText }} />
              )}
              <Text>Dark mode</Text>
            </div>
            <Switch
              checked={isDarkMode}
              onChange={setIsDarkMode}
              size="small"
              checkedChildren={<MoonOutlined />}
              unCheckedChildren={<SunOutlined />}
            />
          </PreferenceRow>
          <PreferenceRow>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: `${theme.marginXS}px`,
              }}
            >
              <EditOutlined style={{ color: theme.colorText }} />
              <Text>Enable line wrapping in editors</Text>
            </div>
            <Switch
              checked={editorSettings.lineWrapping}
              onChange={(checked) =>
                updateEditorSettings({ lineWrapping: checked })
              }
              size="small"
            />
          </PreferenceRow>
        </Section>

        <Divider style={{ margin: `${theme.marginLG}px 0` }} />

        <Section>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: `${theme.margin}px`,
            }}
          >
            <Title level={5} style={{ margin: 0 }}>
              Teams
            </Title>
            <Tooltip title="Hover over team names to see team IDs">
              <InfoCircleOutlined
                style={{ color: theme.colorTextSecondary, cursor: 'help' }}
              />
            </Tooltip>
          </div>
          <Space direction="vertical" style={{ width: '100%' }}>
            {teamUsers?.items?.map((tu) => {
              const team = teamMap.get(tu.teamId);
              return (
                <Tooltip key={tu.teamId} title={`Team ID: ${tu.teamId}`}>
                  <TeamItem>
                    <Text style={{ fontSize: `${theme.fontSize}px` }}>
                      {team?.name || tu.teamId}
                    </Text>
                    <TeamRole $isAdmin={tu.role === 'admin'}>
                      {tu.role}
                    </TeamRole>
                  </TeamItem>
                </Tooltip>
              );
            })}
            {(!teamUsers?.items || teamUsers.items.length === 0) && (
              <Text type="secondary">No teams</Text>
            )}
          </Space>
        </Section>
      </Card>
    </PageContainer>
  );
};
